import { OrthoPalettes } from "@nova/constants"
import { Coordinate } from "@nova/protobuf"
import { convertBboxToExtent } from "@nova/calculate-geospatial"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CogTileExtent, CogTileLayer } from "features/map/layers/cog/CogTileLayer"
import { CogTileRequest, CogTileSource } from "features/map/layers/cog/fetchCogTile"
import { ComparisonClip, OrthoComparisonExtension } from "./OrthoComparisonExtension"
import { OrthoMaskExtension, toMaskPolygon } from "./OrthoMaskExtension"

type OrthoTileQuery = {
    orgId: string
    projectId: string
    flightId: string
    palette?: OrthoPalettes
    isDemo?: boolean
    cogId?: string
    source?: "satellite"
}

const orthoTileSource =
    ({
        orgId,
        projectId,
        flightId,
        palette,
        isDemo,
        cogId,
        source,
    }: OrthoTileQuery): CogTileSource =>
    ({ x, y, z, tileSize }: CogTileRequest, signal: AbortSignal | undefined) => {
        const query = new URLSearchParams(
            Object.entries({
                palette: palette?.toString(),
                tilesize: tileSize.toString(),
                isDemo: isDemo ? "true" : "",
                cogId,
                source,
            }).reduce<Record<string, string>>((acc, [key, value]) => {
                if (value) acc[key] = value
                return acc
            }, {})
        ).toString()

        return fetchApiGateway(
            "get /org/:orgId/project/:projectId/flight/:flightId/tile/:z/:x/:y",
            {
                params: { orgId, projectId, flightId, z, x, y },
                query,
                headers: { Accept: "image/png" },
                rawResponse: true,
                signal,
            }
        )
    }

const retinaTileSize = () =>
    typeof window !== "undefined" && window.devicePixelRatio > 1 ? (512 as const) : (256 as const)

export type CogOrthoLayerOptions = OrthoTileQuery & {
    id: string
    bbox?: Coordinate[]
    minZoom?: number
    maxZoom?: number
    opacity?: number
    comparisonClip?: ComparisonClip
    maskPolygon?: Coordinate[] | null
    transform?: (data: ImageData) => ImageData
}

export const CogOrthoLayer = ({
    id,
    orgId,
    projectId,
    flightId,
    bbox,
    minZoom,
    maxZoom,
    opacity,
    palette,
    isDemo,
    cogId,
    source,
    comparisonClip,
    maskPolygon,
    transform,
}: CogOrthoLayerOptions) => {
    const activeMask = toMaskPolygon(maskPolygon)

    return CogTileLayer({
        id,
        source: orthoTileSource({ orgId, projectId, flightId, palette, isDemo, cogId, source }),
        extent: bbox?.length ? (convertBboxToExtent(bbox) as CogTileExtent) : undefined,
        minZoom,
        maxZoom,
        tileSize: retinaTileSize(),
        maxRequests: 16,
        refinementStrategy: opacity === 100 ? "best-available" : "no-overlap",
        transform,
        additionalExtensions: [
            ...(comparisonClip ? [new OrthoComparisonExtension()] : []),
            ...(activeMask ? [new OrthoMaskExtension()] : []),
        ],
        bitmap: () => ({
            opacity,
            comparisonClip: comparisonClip ?? null,
            maskPolygon: activeMask,
        }),
        invalidateOn: {
            source: [orgId, projectId, flightId, palette, isDemo, cogId, source],
            bitmap: [
                opacity,
                palette,
                comparisonClip?.threshold,
                comparisonClip?.axis,
                comparisonClip?.hideHigh,
                maskPolygon,
                transform,
            ],
        },
    })
}
